import {
  config,
  type ThreadListFilter,
  type ThreadListPageState,
  useSDK,
  useStores,
} from '@littlebird/sdk';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { getArchiveErrorDescription } from '@/features/chat/archive-error';
import { presentSearchCmdBarResults } from '@/features/cmd-bar/search-result-presentation';
import { confirmChatListDeletion } from '@/features/settings/proactive-agent-chat-deletion';
import { trackEvent } from '@/utils/analytics';

import {
  type ChatSearchResult,
  getSearchResultThreadId,
} from './all-chats.types';
import { hasCurrentSearchResults } from './all-chats.utils';

const SEARCH_DEBOUNCE_MS = 300;
const CONFIRM_RESET_MS = 4000;

export function useChatListPagination(filter: ThreadListFilter) {
  const { threads } = useStores();
  const showMessagingChannelChats = config.chat.show_messaging_channel_chats;
  const [queryState, setQueryState] = useState<{
    pageState?: ThreadListPageState;
    hasMore: boolean;
    isLoaded: boolean;
  }>({
    hasMore: false,
    isLoaded: false,
  });

  useEffect(() => {
    let cancelled = false;
    setQueryState({ hasMore: false, isLoaded: false });

    void threads
      .queryInitialListPage(filter)
      .then((page) => {
        if (cancelled) return;
        setQueryState({
          pageState: page.state,
          hasMore: page.hasMore,
          isLoaded: true,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setQueryState({ hasMore: false, isLoaded: true });
        toast.error('Failed to load chats');
      });
    return () => {
      cancelled = true;
    };
  }, [filter, threads, showMessagingChannelChats]);

  const loadNextPage = useCallback(async () => {
    const pageState = queryState.pageState;
    if (!pageState || !queryState.hasMore) return;

    try {
      const page = await threads.queryNextListPage(filter, pageState);

      setQueryState((currentState) => {
        if (currentState.pageState !== pageState) return currentState;
        return {
          pageState: page.state,
          hasMore: page.hasMore,
          isLoaded: true,
        };
      });
    } catch {
      toast.error('Failed to load more chats');
    }
  }, [filter, queryState.hasMore, queryState.pageState, threads]);

  return { ...queryState, loadNextPage };
}

export function useChatSearchResults(
  trimmedQuery: string,
  isSearching: boolean
) {
  const { searchStore } = useSDK();
  const { threads } = useStores();

  const debouncedSearch = useMemo(
    () =>
      debounce(
        (value: string) => searchStore.search(value),
        SEARCH_DEBOUNCE_MS
      ),
    [searchStore]
  );

  useEffect(() => {
    if (isSearching) {
      debouncedSearch(trimmedQuery);
    }
    return () => debouncedSearch.cancel();
  }, [trimmedQuery, isSearching, debouncedSearch]);

  const hasCurrentResults = hasCurrentSearchResults(
    trimmedQuery,
    searchStore.query,
    searchStore.isLoading
  );
  if (!isSearching || !hasCurrentResults) {
    return {
      results: [] as ChatSearchResult[],
      isLoading: isSearching && !hasCurrentResults,
    };
  }

  const results = presentSearchCmdBarResults({
    searchResults: searchStore.results,
    query: trimmedQuery,
    getThreadTitle: (threadId) => threads.get(threadId)?.title,
    getRoutineTitle: () => undefined,
  })
    .filter(
      (item): item is ChatSearchResult =>
        item.type === 'thread' || item.type === 'message'
    )
    .filter((item) => item.reportTemplateId === undefined)
    // TODO: hacky fix ideally should be removed from search index
    .filter((item) => {
      const thread = threads.get(getSearchResultThreadId(item));
      if (!thread) return true;
      return thread.isAvailableForInteraction;
    });

  return { results, isLoading: false };
}

export function useChatBulkSelection(
  chatIds: string[],
  archiveMode: 'archive' | 'unarchive'
) {
  const { threads } = useStores();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const selectionActive = selectedIds.size > 0;

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((previousIds) => {
      const nextIds = new Set(previousIds);
      if (nextIds.has(id)) nextIds.delete(id);
      else nextIds.add(id);
      return nextIds;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setConfirmingDelete(false);
    clearTimeout(confirmTimerRef.current);
  }, []);

  const handleBulkDelete = useCallback(async () => {
    if (isDeleting) return;
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      confirmTimerRef.current = setTimeout(
        () => setConfirmingDelete(false),
        CONFIRM_RESET_MS
      );
      return;
    }

    clearTimeout(confirmTimerRef.current);
    const ids = [...selectedIds];
    if (!(await confirmChatListDeletion(ids))) {
      setConfirmingDelete(false);
      return;
    }
    setIsDeleting(true);
    try {
      await Promise.all(
        ids.map(async (id) => {
          const thread = await threads.ensureLoaded(id);
          if (thread) await threads.delete(thread);
        })
      );
      trackEvent('CHAT_DELETED', { type: 'bulk' });
      toast.success(`Deleted ${ids.length} chat${ids.length === 1 ? '' : 's'}`);
      clearSelection();
    } catch {
      toast.error('Failed to delete chats');
      setConfirmingDelete(false);
    } finally {
      setIsDeleting(false);
    }
  }, [confirmingDelete, isDeleting, selectedIds, clearSelection, threads]);

  const handleBulkArchiveOrRestore = useCallback(async () => {
    if (isArchiving) return;
    const ids = [...selectedIds];
    setIsArchiving(true);
    try {
      await Promise.all(
        ids.map(async (id) => {
          const thread = await threads.ensureLoaded(id);
          if (!thread) return;
          if (archiveMode === 'unarchive') {
            await threads.unarchiveThread(thread);
            return;
          }
          await threads.archiveThread(thread);
        })
      );
      if (archiveMode === 'archive') {
        trackEvent('CHAT_ARCHIVED', { type: 'bulk' });
      }
      toast.success(
        `${archiveMode === 'archive' ? 'Archived' : 'Restored'} ${ids.length} chat${ids.length === 1 ? '' : 's'}`
      );
      clearSelection();
    } catch (error) {
      toast.error(
        archiveMode === 'archive'
          ? 'Failed to archive chats'
          : 'Failed to restore chats',
        {
          description: getArchiveErrorDescription(error, archiveMode),
        }
      );
    } finally {
      setIsArchiving(false);
    }
  }, [archiveMode, isArchiving, selectedIds, clearSelection, threads]);

  useEffect(() => () => clearTimeout(confirmTimerRef.current), []);

  useEffect(() => {
    if (!selectionActive) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') clearSelection();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selectionActive, clearSelection]);

  useEffect(() => {
    if (!selectionActive) return;
    const chatIdSet = new Set(chatIds);
    setSelectedIds((previousIds) => {
      const nextIds = new Set(
        [...previousIds].filter((id) => chatIdSet.has(id))
      );
      return nextIds.size === previousIds.size ? previousIds : nextIds;
    });
  }, [chatIds, selectionActive]);

  return {
    selectedIds,
    selectionActive,
    toggleSelect,
    clearSelection,
    confirmingDelete,
    isArchiving,
    isDeleting,
    handleBulkDelete,
    handleBulkArchiveOrRestore,
  };
}
